import * as React from "react"

/**
 * Shared nesting state for <Dialog> and <AlertDialog>.
 *
 * Every dialog root registers itself with the dialog root above it while it is
 * open. A dialog that has at least one open child renders `data-child-open` on
 * its content, which pushes it back + up (see `dialogPushBackClasses`).
 *
 * Both dialog types use this same context, so any combination nests correctly:
 * dialog-in-dialog, alert-in-dialog, dialog-in-alert, alert-in-alert.
 */
type DialogNestingContextValue = {
  /** True while at least one nested dialog below this one is open. */
  hasOpenChild: boolean
  /** Called by a nested dialog while it is open; returns the unregister fn. */
  registerOpenChild: () => () => void
}

const DialogNestingContext =
  React.createContext<DialogNestingContextValue | null>(null)

type DialogRootOpenProps = {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Used by a dialog root. Returns the nesting value to publish to its subtree
 * and the `onOpenChange` handler to hand to the Radix root.
 */
function useDialogNestingRoot({
  open,
  defaultOpen,
  onOpenChange,
}: DialogRootOpenProps) {
  const parent = React.useContext(DialogNestingContext)
  const [openChildCount, setOpenChildCount] = React.useState(0)
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false
  )

  // Works for both controlled and uncontrolled usage.
  const isOpen = open ?? uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange]
  )

  // Tell the dialog above us that we're open, so it can push itself back.
  // Depend on the (stable) register fn, not the context object — that object's
  // identity changes whenever the parent's own child count does.
  const registerWithParent = parent?.registerOpenChild
  React.useEffect(() => {
    if (!registerWithParent || !isOpen) return
    return registerWithParent()
  }, [registerWithParent, isOpen])

  const registerOpenChild = React.useCallback(() => {
    setOpenChildCount((count) => count + 1)
    return () => setOpenChildCount((count) => Math.max(0, count - 1))
  }, [])

  const nesting = React.useMemo<DialogNestingContextValue>(
    () => ({ hasOpenChild: openChildCount > 0, registerOpenChild }),
    [openChildCount, registerOpenChild]
  )

  return { nesting, handleOpenChange }
}

/** Used by a dialog content: true while one of its own children is open. */
function useHasOpenChildDialog() {
  return React.useContext(DialogNestingContext)?.hasOpenChild ?? false
}

/**
 * Push-back styling for a centered dialog content (one that positions itself
 * with `-translate-x-1/2 -translate-y-1/2`). Deliberately avoids the
 * `duration-*` utility: that one also sets `--tw-duration`, which would retime
 * the open/close animation.
 */
const dialogPushBackClasses =
  "transition-[translate,scale] [transition-duration:300ms] ease-out data-[child-open]:scale-95 data-[child-open]:translate-y-[calc(-50%_-_1.5rem)]"

export {
  DialogNestingContext,
  dialogPushBackClasses,
  useDialogNestingRoot,
  useHasOpenChildDialog,
}
